// frontend-admin/src/lib/master-api.js
//
// Axios client for Master (platform) admin. Uses a separate localStorage token
// key ("master_token") so that it doesn't collide with the store admin session.

import axios from 'axios';

const BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000') + '/api/v1/master';

const masterApi = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

masterApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('master_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

masterApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const url = error.config?.url || '';
      if (!url.includes('/me')) {
        localStorage.removeItem('master_token');
        localStorage.removeItem('master_admin');
        if (window.location.pathname.startsWith('/master')) {
          window.location.href = '/master/login';
        }
      }
    }
    return Promise.reject(error);
  },
);

// ---------- Auth helpers ----------
export async function loginMaster(email, password) {
  const res = await masterApi.post('/login', { email, password });
  const { token, admin } = res.data;
  localStorage.setItem('master_token', token);
  localStorage.setItem('master_admin', JSON.stringify(admin));
  return { token, admin };
}

export function logoutMaster() {
  localStorage.removeItem('master_token');
  localStorage.removeItem('master_admin');
  window.location.href = '/master/login';
}

export function getMasterAdmin() {
  try {
    return JSON.parse(localStorage.getItem('master_admin') || 'null');
  } catch {
    return null;
  }
}

export function isMasterAuthenticated() {
  return !!localStorage.getItem('master_token');
}

// ---------- Convenience wrappers ----------
export const masterFetchDashboard   = () => masterApi.get('/dashboard').then((r) => r.data);
export const masterFetchStores      = (params) => masterApi.get('/stores', { params }).then((r) => r.data);
export const masterFetchStoreDetail = (id) => masterApi.get(`/stores/${id}`).then((r) => r.data);
export const masterSuspendStore     = (id) => masterApi.put(`/stores/${id}/suspend`).then((r) => r.data);
export const masterActivateStore    = (id) => masterApi.put(`/stores/${id}/activate`).then((r) => r.data);
export const masterChangePlan       = (id, plan_id) => masterApi.put(`/stores/${id}/plan`, { plan_id }).then((r) => r.data);
export const masterFetchSubscriptions = (params) => masterApi.get('/subscriptions', { params }).then((r) => r.data);
export const masterFetchInvoices    = (params) => masterApi.get('/invoices', { params }).then((r) => r.data);
export const masterFetchRevenue     = () => masterApi.get('/revenue').then((r) => r.data);
export const masterPostAnnouncement = (payload) => masterApi.post('/announcements', payload).then((r) => r.data);
export const masterFetchAnnouncements = () => masterApi.get('/announcements').then((r) => r.data);
export const masterFetchAudit       = () => masterApi.get('/audit').then((r) => r.data);

export default masterApi;
